<template>
    <div
        ref="container"
        tabindex="0"
        :class="{ opened: isOpened }"
        class="tl-drawer"
        data-testid="drawer"
    >
        <div
            class="backdrop"
            @click="close"
        ></div>
        <div class="tl-drawer-close-button">
            <Button
                icon="pi pi-times"
                size="small"
                severity="secondary"
                data-testid="drawer-close-button"
                @click="close"
            />
        </div>
        <div
            v-if="drawerProps.icon || drawerProps.title"
            class="tl-drawer-header"
        >
            <div v-if="typeof drawerProps.icon === 'string'">
                <i
                    :class="drawerProps.icon"
                    class="tl-drawer-icon"
                ></i>
            </div>
            <div
                v-else-if="drawerProps?.icon"
                class="tl-drawer-icon"
                data-testid="drawer-icon"
            >
                <DashboardIcon v-bind="{ ...drawerProps.icon, size: 40 }" />
            </div>
            <div class="tl-drawer-header-text">
                <div
                    v-if="drawerProps.title"
                    class="text-xl font-bold"
                    data-testid="drawer-title"
                >
                    {{ drawerProps.title }}
                </div>
            </div>
        </div>
        <div
            class="tl-drawer-container"
            data-testid="drawer-container"
        >
            <slot :is-opened="isOpened">
                <Component
                    :is="drawerComponent.component"
                    v-if="drawerComponent?.component"
                    v-bind="drawerComponent?.bindings"
                    v-on="drawerComponent?.listeners || {}"
                />
            </slot>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { DrawerComponent, DrawerOpenProps } from '@/types/components'
import { onMounted, ref, watch } from 'vue'
import DashboardIcon from './DashboardIcon.vue'
import { useScrollLock } from '@vueuse/core'
import { isNil, isNotNil } from 'ramda'
import { computed } from 'vue'

const openModel = ref(false)
const drawerComponent = ref<DrawerComponent>()
const container = ref<HTMLElement | null>(null)
const drawerResolve = ref()
const lockBodyOverflow = useScrollLock(document.querySelector('body'))

const props = withDefaults(defineProps<DrawerOpenProps & {
    opened?: boolean
}>(), {
    opened: undefined,
})

const drawerProps = ref<DrawerOpenProps>({})

const emit = defineEmits<{
    (e: 'close'): void
}>()

const open = (
    component?: DrawerComponent,
    drawerPropsArg?: DrawerOpenProps,
) => {
    if (!isNil(props.opened)) {
        return Promise.resolve()
    }

    if (component) {
        drawerComponent.value = component
    }
    if (drawerPropsArg) {
        drawerProps.value = drawerPropsArg || {}
    }
    openModel.value = true

    lockBodyOverflow.value = true

    return new Promise((resolve) => {
        drawerResolve.value = resolve
    })
}

const close = () => {
    emit('close')

    if (!isNil(props.opened)) {
        return
    }

    openModel.value = false
    if (isNotNil(drawerComponent.value)) {
        drawerComponent.value = undefined
    }

    lockBodyOverflow.value = false

    if (drawerResolve.value) {
        drawerResolve.value({ success: true })
    }
}

if (isNotNil(props.opened)) {
    watch(() => props.opened, (val) => {
        lockBodyOverflow.value = !!val
    })
}

const isOpened = computed(() => {
    if (!isNil(props.opened)) {
        return props.opened
    }

    return openModel.value
})

defineExpose({ open, close })

onMounted(() => {
    drawerProps.value = {
        ...props,
    }
})
</script>

<style lang="sass" scoped>
.tl-drawer-container
    overflow: auto
    flex: 1
    position: relative

.tl-drawer
    --width: 500px
    --padding-container: 30px 24px 12px
    background: var(--tl-color-base-white)
    border-left: 1px solid var(--tl-color-border)
    height: 100%
    opacity: 0
    position: fixed
    right: calc(-1 * var(--width))
    top: 0
    transition: 0.2s ease-in-out all
    width: var(--width)
    z-index: var(--layer-drawer)
    display: flex
    flex-direction: column

    .backdrop
        content: ''
        opacity: 0
        display: block
        position: absolute
        top: 0
        right: 0
        bottom: 0
        left: 0
        background: rgba(0,0,0,0)
        transition: background-color .3s .1s ease-in-out, backdrop-filter .3s .1s ease-in-out

    &.opened
        opacity: 1
        right: 0

        .backdrop
            content: ''
            opacity: 1
            left: calc(-100vw + var(--width))
            right: var(--width)
            background: var(--color-black-tint-medium)
            backdrop-filter: blur(2px)

    &-header
        @apply p-5
        display: flex
        align-items: center
        border-bottom: 1px solid var(--tl-color-border)

    &-container
        padding: var(--padding-container)

    &-icon
        margin-right: 16px
        font-size: 20px

    &-close-button
        position: absolute
        right: 0
        top: 0
        padding: 8px
        z-index: 101
</style>
